import React from 'react';
import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material';
import { Users } from 'lucide-react';
import { TeamDot } from '../../../components/floorplan/teamColors';
import { sbg } from '../../../theme/sbgTokens';
import { formatCount } from '../../../utils/format';

export interface TeamHighlightOption {
  name: string;
  /** The team's identity color (hex) from the API. */
  color?: string;
  /** Desks this team holds on the floor in view. */
  deskCount: number;
}

interface TeamHighlightProps {
  /** Every team, teams on this floor first (most desks first), then the rest. */
  teams: TeamHighlightOption[];
  value: string | null;
  onChange: (teamName: string | null) => void;
}

/**
 * Pick one team to pick out on the floor. Its desks keep their team color,
 * drawn stronger; every other desk steps back to neutral. Options lead with
 * the teams seated on this floor and show each team's color.
 */
const TeamHighlight: React.FC<TeamHighlightProps> = ({ teams, value, onChange }) => {
  const selected = value ? teams.find((team) => team.name === value) ?? { name: value, deskCount: 0 } : null;

  return (
    <Autocomplete<TeamHighlightOption, false, false, false>
      options={teams}
      value={selected}
      onChange={(_event, team) => onChange(team?.name ?? null)}
      getOptionLabel={(team) => team.name}
      isOptionEqualToValue={(option, current) => option.name === current.name}
      autoHighlight
      noOptionsText="No team matches that search"
      renderOption={(props, team) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <li key={key} {...optionProps}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%', minWidth: 0 }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <TeamDot color={team.color} />
                <Typography variant="body1" component="span" noWrap sx={{ color: sbg.text.primary }}>
                  {team.name}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                component="span"
                sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                {team.deskCount ? formatCount(team.deskCount, 'desk') : 'Not on this floor'}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Highlight a team"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ color: sbg.text.secondary, ml: 0.5 }}>
                {selected?.color ? <TeamDot color={selected.color} size={10} /> : <Users size={16} aria-hidden="true" />}
              </InputAdornment>
            ),
          }}
          inputProps={{ ...params.inputProps, 'aria-label': 'Highlight a team on the floor plan' }}
        />
      )}
      sx={{
        width: { xs: '100%', sm: 224 },
        '& .MuiOutlinedInput-root': { height: 34, py: 0 },
      }}
    />
  );
};

export default TeamHighlight;
